import { Injectable, Optional } from '@angular/core';
import { TransferState, makeStateKey } from '@angular/platform-browser';

import { EnvironmentService } from '../environment/environment.service';
import { DevBuild } from '../environment/environment.types';
import { PlatformService } from '../platform/platform.service';
import { WindowRefService } from '../window-ref/window-ref.service';

type Toggles = Record<string, boolean>;

const STATE_KEY = makeStateKey<string[]>('featureToggle.state');

export function cleanToggleClassName(className: string): string {
  return className.replace(/\./g, '-');
}

export function activeTogglesToArray(toggles: Toggles): string[] {
  return Object.keys(toggles).filter(key => toggles[key] === true);
}

@Injectable({
  providedIn: 'root'
})
export class FeatureToggleService {
  private readonly featureToggleEngine: FeatureToggleEngine;
  private readonly windowRef: any;

  constructor(
    @Optional() private readonly transferState: TransferState,
    { featureToggles, isStandalone, devBuild }: EnvironmentService,
    { nativeWindow }: WindowRefService,
    { isServer }: PlatformService
  ) {
    this.windowRef = nativeWindow;
    const isDevBuild = devBuild === DevBuild.NGC;
    const toggles = this.transferToggles(isServer, isStandalone, isDevBuild, featureToggles);
    const addClasses = isServer || isDevBuild;

    this.featureToggleEngine = toggles
      ? new DefaultFeatureToggleEngine(toggles, addClasses, nativeWindow)
      : this.windowRef.featureToggleEngine;
  }

  getToggleById(id: string): boolean {
    try {
      // ALWAYS cast to boolean but never return undefined
      return !!this.featureToggleEngine.getToggleById(id);
    } catch {
      return false;
    }
  }

  setToggle(id: string, value: boolean): void {
    this.featureToggleEngine.setToggle(id, value);
  }

  togglesLoaded(): Promise<void> {
    return new Promise<void>(resolve => {
      if (this.featureToggleEngine && !this.featureToggleEngine.togglesLoaded) {
        const loadedHandler = (): void => {
          resolve();
          this.windowRef.document.removeEventListener(this.featureToggleEngine.loadedEvent, loadedHandler);
        };

        this.windowRef.document.addEventListener(this.featureToggleEngine.loadedEvent, loadedHandler);
      } else {
        resolve();
      }
    });
  }

  /**
   * Add/read the toggles to/from TransferState (Universal -> client transition).
   * In order to keep the page size low, before transferring the toggle data
   * it will be converted to an array of active (true) toggle keys only.
   *
   * @param isServer
   * @param isStandalone
   * @param isDevBuild
   * @param toggles
   * @private
   */
  private transferToggles(isServer: boolean, isStandalone: boolean, isDevBuild: boolean, toggles: Toggles): Toggles {
    if (!isStandalone || !this.transferState || isDevBuild) {
      return toggles;
    }
    if (isServer) {
      this.transferState.onSerialize(STATE_KEY, () => activeTogglesToArray(toggles));
      return toggles;
    } else {
      return this.transferState.get<string[]>(STATE_KEY, []).reduce((acc, key) => ({ ...acc, [key]: true }), {});
    }
  }
}

export interface FeatureToggleEngine {
  loadedEvent: string;
  togglesLoaded: boolean;
  getToggleById(id: string): boolean;
  setToggle(id: string, value: boolean);
}

class DefaultFeatureToggleEngine implements FeatureToggleEngine {
  loadedEvent = null;
  togglesLoaded = true;

  constructor(private readonly toggles: Toggles, addClasses: boolean, win: Window) {
    addClasses && this.addToggleClasses(toggles, win);
  }

  getToggleById(id: string): boolean {
    return !!this.toggles[id];
  }

  setToggle(id: string, value: boolean): void {
    this.toggles[id] = value;
  }

  /**
   * Toggle key class names to be applied in universal env only,
   * they will persist also in the client, so no need
   * to apply them on client again.
   * @param toggles
   * @param win
   * @private
   */
  private addToggleClasses(toggles: Toggles, win: Window): void {
    win.document.documentElement.classList.add(...activeTogglesToArray(toggles).map(key => cleanToggleClassName(key)));
  }
}
